<template>
  <header
    class="site-header"
    ref="headerEl"
  >
    
    <div 
      class="castle-layer"
      :class="{ 'castle--visible': showCastleLogo && showHeaderElements, 'castle--hidden': !showCastleLogo || !showHeaderElements }"
    >
      <img
        :src="header.logoCastle?.image"
        alt=""
      />
    </div>
    
    <nav class="site-menu" ref="menuEl">
      
      <div class="logo-main">
        <a :href="header.logoMain?.link || '/'">
          <img
            :src="header.logoMain?.image"
            :alt="header.logoMain?.alt"
          />
        </a>
      </div>

      <ul class="main-menu">
        <li
          v-for="item in header.menuItems"
          :key="item.link"
          @mouseenter="setHeroCover(item.coverIndex ?? 0)"
          @focus="setHeroCover(item.coverIndex ?? 0)"
        >
          <a :href="item.link">{{ item.label }}</a>
        </li>
      </ul>

      <div class="menu-right">
        
        <ul class="action-icons">
          <li v-for="icon in header.actionIcons" :key="icon.link || icon.label">
            <a 
              :href="getActionLink(icon)" 
              :aria-label="icon.ariaLabel"
              :target="icon.target || '_self'"
              :rel="icon.rel || ''"
            >
              <img :src="icon.icon" :alt="'Icona ' + icon.label" />
              <span>{{ icon.label }}</span>
            </a>
          </li>
        </ul>

        <button
          class="hamburger"
          @click="showMegaMenu = true"
          :aria-label="header.hamburger?.ariaLabel"
        >
          <img :src="header.hamburger?.icon" :alt="header.hamburger?.alt" />
        </button>

        <div class="logo-secondary">
          <a 
            :href="header.logoSecondary?.link" 
            :target="header.logoSecondary?.target" 
            :rel="header.logoSecondary?.rel"
          >
            <img
              class="logo-foundation"
              :class="{ 'logo--transparent': showCastleLogo && showHeaderElements, 'logo--visible': !showCastleLogo || !showHeaderElements }"
              :src="header.logoSecondary?.image"
              :alt="header.logoSecondary?.alt"
            />
          </a>
        </div>
      </div>

      <MegaMenu 
        :show="showMegaMenu" 
        :closeMenu="closeMegaMenu"
        :menuItems="megaMenuItems" 
      />
    </nav>

    <div 
      class="header-base"
      :class="{ 'header-base--hidden': !showHeaderElements }"
    >
      <img
        :src="header.headerBase?.image"
        alt=""
      />
    </div>
  </header>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { createResizeObserver } from '@/utils/legacySupport'
import { state } from '@/store'
import MegaMenu from '@/components/MegaMenu.vue'
import { useFoundationLogoVisibility } from '@/utils/useFoundationLogoVisibility.js'
import { useHeroCoverSwitcher } from '@/utils/useHeroCoverSwitcher.js'
import { useHeaderFooterVisibility } from '@/utils/useHeaderFooterVisibility.js'

// ───────────────────────────────────────────────────────────────────
// REACTIVE STATE & REFS
// ───────────────────────────────────────────────────────────────────

/** Controls mega menu visibility */
const showMegaMenu = ref(false)

/** Closes mega menu callback */
const closeMegaMenu = () => (showMegaMenu.value = false)

/** Reference to header element */
const headerEl = ref(null)

/** Reference to menu nav element for height calculations */
const menuEl = ref(null)

// ───────────────────────────────────────────────────────────────────
// COMPOSABLES & UTILITIES
// ───────────────────────────────────────────────────────────────────

/** Determines if header should be visible (when footer not in view) */
const { showHeader: showHeaderElements } = useHeaderFooterVisibility()

/** Gets hero cover switcher utility for menu item interactions */
const { setHeroCover } = useHeroCoverSwitcher()

/** Gets foundation/castle logo visibility logic */
const { showCastleLogo: showCastleOnScroll } = useFoundationLogoVisibility()

// ───────────────────────────────────────────────────────────────────
// COMPUTED PROPERTIES
// ───────────────────────────────────────────────────────────────────

/** Header data from global store */
const header = computed(() => state.header)

/** Mega menu items from global store */
const megaMenuItems = computed(() => state.megaMenuItems)

/**
 * Detects if current page is homepage
 * Used to determine if castle logo should follow scroll or always show
 */
const isHomePage = computed(() => {
  return window.location.pathname === '/' || 
         window.location.pathname === '/fondazione' || 
         window.location.pathname === '/fondazione/' || 
         window.location.pathname === '/home'
})

/**
 * Determines castle logo visibility
 * - Homepage: Show/hide based on scroll position (via scroll observer)
 * - Other pages: Always visible (when header is in view)
 */
const showCastleLogo = computed(() => {
  return isHomePage.value ? showCastleOnScroll.value : true
})

// ───────────────────────────────────────────────────────────────────
// PLATFORM & LINK RESOLUTION
// ───────────────────────────────────────────────────────────────────

/**
 * Detects current platform using multiple detection methods
 * 
 * @returns {String} 'ios' | 'android' | 'desktop'
 */
function getPlatform() {
  const ua = navigator.userAgent || navigator.vendor || window.opera || ''
  const uaDataPlatform = (navigator.userAgentData && navigator.userAgentData.platform) 
    ? navigator.userAgentData.platform.toLowerCase() 
    : ''

  let isAndroid = /Android/i.test(ua) || uaDataPlatform.includes('android')
  let isIOS = /iPhone|iPad|iPod/i.test(ua) || uaDataPlatform.includes('ios')

  // iPadOS 13+ presents as "Macintosh" but has touch support
  const maybeIPadOS13 = !isIOS && /Macintosh/i.test(ua) && 
    navigator.maxTouchPoints && navigator.maxTouchPoints > 1
  if (maybeIPadOS13) isIOS = true

  if (isIOS) return 'ios'
  if (isAndroid) return 'android'
  return 'desktop'
}

/**
 * Resolves platform-specific link for CRFOnline action
 * Applies fallback chain: platform-specific → generic → default
 * 
 * @param {Object} icon - Action icon object with platform-specific links
 * @returns {String} Resolved link URL
 */
function resolvePlatformLink(icon) {
  const platform = getPlatform()
  const p = icon.platforms || {}
  const desktopLink = p.desktop || icon.linkDesktop || icon.link || '#'
  const androidLink = p.android || icon.linkAndroid || icon.linkMobile || icon.link || desktopLink
  const iosLink = p.ios || icon.linkIOS || icon.link || desktopLink

  if (platform === 'ios') return iosLink || desktopLink
  if (platform === 'android') return androidLink || desktopLink
  return desktopLink
}

/**
 * Gets appropriate link for action icon
 * CRFOnline icons use platform detection; others use standard link
 * 
 * @param {Object} icon - Icon configuration object
 * @returns {String} Link URL
 */
function getActionLink(icon) {
  if (icon.label === 'CRFOnline') {
    return resolvePlatformLink(icon)
  }
  return icon.link || '#'
}

// ───────────────────────────────────────────────────────────────────
// HEADER HEIGHT CALCULATION
// ───────────────────────────────────────────────────────────────────

/**
 * Calculates and updates header height CSS custom properties
 * Called on mount, resize, and ResizeObserver changes
 * Updates: --header-nav-height and --header-total-height
 */
const updateHeaderHeight = () => {
  nextTick(() => {
    if (menuEl.value) {
      const actualHeight = menuEl.value.offsetHeight
      document.documentElement.style.setProperty('--header-nav-height', `${actualHeight}px`)
      document.documentElement.style.setProperty('--header-total-height', `${actualHeight + 1}px`)
    }
  })
}

// ───────────────────────────────────────────────────────────────────
// LIFECYCLE HOOKS
// ───────────────────────────────────────────────────────────────────

/**
 * Initialize header on mount
 * - Calculate initial header height
 * - Setup resize event listener
 * - Setup ResizeObserver for dynamic height changes
 */
onMounted(() => {
  // Initial height calculation
  updateHeaderHeight()
  
  // Recalculate on window resize
  window.addEventListener('resize', updateHeaderHeight)
  
  // Delayed calculation to ensure render completion
  setTimeout(updateHeaderHeight, 100)
  
  // ResizeObserver for menu element height changes.
  // ResizeObserver esiste da iOS 13.4: sotto quella versione `new
  // ResizeObserver(...)` lancerebbe un errore e si porterebbe via il
  // montaggio dell'header. L'helper restituisce null e si prosegue: senza
  // observer l'altezza non si ricalcola quando il menu cambia da solo, ma
  // il ricalcolo su `resize` resta attivo.
  const resizeObserver = createResizeObserver(() => {
    updateHeaderHeight()
  })
  
  if (resizeObserver && menuEl.value) {
    resizeObserver.observe(menuEl.value)
  }
  
  onBeforeUnmount(() => {
    if (resizeObserver) resizeObserver.disconnect()
  })
})

/**
 * Clean up event listeners on unmount
 * Prevents memory leaks from resize listener
 */
onBeforeUnmount(() => {
  window.removeEventListener('resize', updateHeaderHeight)
})
</script>

<style scoped lang="scss">
@import '@/assets/styles/main.scss';

/* ------------------------------------------------------------------
   Stili base dell'header - sempre visibile
------------------------------------------------------------------ */
.site-header {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 151;
  background: $surface-base;
  overflow: visible;
  border-bottom: 1px solid $border-color;

  /* ------------------------------------------------------------------
     Layer del castello
  ------------------------------------------------------------------ */
  .castle-layer {
    position: absolute;
    bottom: -2px;
    right: 0px;
    top: 10px;
    height: auto;
    width: auto;
    z-index: -1;
    pointer-events: none;
    overflow: hidden;
    
    @media (max-width: 1024px) {
      display: none;
    }
    
    img {
      height: 100%;
      width: auto;
      object-fit: contain;
      object-position: bottom right;
      transition: opacity 0.4s ease-in-out, transform 0.4s ease-in-out;
    }
    
    &.castle--visible img {
      opacity: 1;
      transform: translateX(0);
    }
    
    &.castle--hidden img {
      opacity: 0;
      transform: translateX(20px);
    }
  }

  .header-base {
    position: absolute;
    left: 0;
    top: 100%;
    transform: none;  
    width: 100%;
    pointer-events: none;
    height: auto;
    transition: opacity 0.4s ease-in-out, transform 0.4s ease-in-out;

    img {
      width: 100%;
      height: auto;
      display: block;
    }
    
    &.header-base--hidden {
      opacity: 0;
      transform: translateY(-10px);
    }
  }
}

/* ------------------------------------------------------------------
   Layout principale del menu
------------------------------------------------------------------ */
.site-menu {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: $header-padding-y $header-padding-x-right $header-padding-y $header-padding-x-left;
  @include flex-gap($space-4);
  width: 100%;
  max-width: $header-max-width;
  margin: 0 auto;
  min-height: 64px;  /* Altezza minima per consistenza */

  .logo-main img {
    max-height: $header-logo-height;
    width: auto;
  }

  .logo-secondary img {
    max-height: $header-logo-secondary-height;
    width: auto;
  }

  .main-menu {
    display: flex;
    @include flex-gap(clamp(2.5rem, 6vw, 8rem));
    list-style: none;
    margin: 0;
    padding: 0;
    font-size: $font-menu-main;
    font-weight: 500;
    flex-grow: 1;
    justify-content: center;

    li a {
      position: relative;
      text-decoration: none;
      color: $text-color;
      padding-bottom: 0.25rem;
      transition: color 0.3s ease-in-out, transform 0.3s ease-in-out;
      display: inline-block;
      transform-origin: center center;

      @media (hover: hover) {
        &:hover {
          color: $clr-tertiary;
          transform: scale($font-menu-hover-scale);
        }
      }
    }
  }

  .menu-right {
    display: flex;
    align-items: center;
    @include flex-gap($space-1-5);

    .action-icons {
      display: flex;
      @include flex-gap($space-1-5);
      list-style: none;

      a {
        display: flex;
        flex-direction: column;
        align-items: center;
        @include flex-gap($space-0-5, column);
        text-decoration: none;
        color: $clr-brand-dark;
        transition: transform 0.3s ease-in-out;
        transform-origin: center center;

        img {
          width: 36px;
          height: 36px;
          filter: brightness(0) saturate(100%) invert(18%) sepia(25%) saturate(1729%) hue-rotate(134deg) brightness(95%) contrast(89%);
          transition: filter 0.3s ease-in-out, transform 0.3s ease-in-out;
        }
        span {
          font-size: 0.95rem;
          color: $clr-brand-dark;
          transition: color 0.3s ease-in-out, transform 0.3s ease-in-out;
        }
        
        &:hover {
          // Fallback
        }

        @media (hover: hover) {
          &:hover {
            transform: scale($font-menu-hover-scale);
            
            img {
              filter: brightness(0) saturate(100%) invert(39%) sepia(52%) saturate(449%) hue-rotate(75deg) brightness(95%) contrast(89%);
            }
            span {
              color: $clr-tertiary;
            }
          }
        }
      }
    }

    .hamburger {
      background: none;
      border: none;
      padding: 0;
      width: 36px;
      height: 32px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: transform 0.3s ease-in-out;
      transform-origin: center center;

      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        filter: brightness(0) saturate(100%) invert(18%) sepia(25%) saturate(1729%) hue-rotate(134deg) brightness(95%) contrast(89%);
        transition: filter 0.3s ease-in-out;
      }
      
      @media (hover: hover) {
        &:hover {
          transform: scale($font-menu-hover-scale);
          
          img {
            filter: brightness(0) saturate(100%) invert(39%) sepia(52%) saturate(449%) hue-rotate(75deg) brightness(95%) contrast(89%);
          }
        }
      }
    }

    .logo-secondary {
      position: relative;
      
      img {
        height: $header-logo-secondary-height;
        transition: opacity 0.4s ease-in-out, transform 0.3s ease-in-out;
        
        &.logo--visible {
          opacity: 1;
          transform: scale(1);
          visibility: visible;
        }
        
        &.logo--transparent {
          opacity: 0;
          transform: scale(0.95);
          visibility: hidden;
          
          @media (max-width: 1024px) {
            position: absolute;
            right: 0;
          }
        }
      }
    }
  }
}

/* ------------------------------------------------------------------
   Media queries responsive - Display medio-grande (1300px)
------------------------------------------------------------------ */
@media (max-width: 1300px) {
  .site-menu {
    padding: $header-padding-y $space-2-5 $header-padding-y $space-1-5;
    @include flex-gap($space-3);

    .logo-main img {
      max-height: 46px;
    }

    .logo-secondary img {
      max-height: 46px;
    }

    .main-menu {
      font-size: $font-menu-main-lg;
      font-weight: 500;
      @include flex-gap(clamp(2rem, 5vw, 7rem));
    }

    .menu-right {
      @include flex-gap($space-1);

      .action-icons {
        @include flex-gap($space-1);
        
        a {
          img {
            width: 32px;
            height: 32px;
          }
          span {
            font-size: 0.85rem;
          }
        }
      }

      .hamburger {
        width: 32px;
        height: 28px;
      }

      .logo-secondary img {
        height: 44px;
      }
    }
  }
}

/* ------------------------------------------------------------------
   Media queries responsive - Tablet (1024px)
------------------------------------------------------------------ */
@media (max-width: 1024px) {
  .main-menu,
  .logo-secondary {
    display: none !important;
  }

  .site-menu {
    padding: $header-padding-y $space-1-5 $header-padding-y $space-1;
    @include flex-gap($space-2);

    .logo-main img {
      max-height: 44px;
    }

    .menu-right {
      .action-icons {
        @include flex-gap($space-1);
        
        a {
          img {
            width: 30px;
            height: 30px;
          }
          span {
            font-size: 0.8rem;
          }
        }
      }

      .hamburger {
        width: 30px;
        height: 26px;
      }
    }
  }
}

/* ------------------------------------------------------------------
   Media queries responsive - Tablet piccolo (768px)
------------------------------------------------------------------ */
@media (max-width: 768px) {
  .site-menu {
    padding: $space-0-5 $space-1 $space-0-5 $space-0-75;
    @include flex-gap($space-1-5);

    .logo-main img {
      max-height: 42px;
    }

    .menu-right {
      .action-icons a {
        img {
          width: 26px;
          height: 26px;
        }
        span {
          font-size: 0.75rem;
        }
      }

      .hamburger {
        width: 28px;
        height: 24px;
      }
    }
  }
}

/* ------------------------------------------------------------------
   Media queries responsive - Mobile (480px)
------------------------------------------------------------------ */
@media (max-width: 480px) {
  .site-menu {
    padding: $space-0-5 $space-0-75 $space-0-5 $space-0-5;
    @include flex-gap($space-1);

    .logo-main img {
      max-height: 38px;
    }

    .menu-right {
      .action-icons {
        @include flex-gap($space-0-75);
        
        a {
          img {
            width: 24px;
            height: 24px;
          }
          span {
            font-size: 0.7rem;
          }
        }
      }

      .hamburger {
        width: 26px;
        height: 22px;
      }
    }
  }
}

/* ------------------------------------------------------------------
   Media queries responsive - Mobile molto piccolo (360px)
------------------------------------------------------------------ */
@media (max-width: 360px) {
  .site-menu {
    padding: $space-0-5 $space-0-5;
    @include flex-gap($space-0-75);

    .logo-main img {
      max-height: 36px;
    }

    .menu-right {
      .action-icons a {
        img {
          width: 22px;
          height: 22px;
        }
        span {
          font-size: 0.65rem;
        }
      }

      .hamburger {
        width: 24px;
        height: 20px;
      }
    }
  }
}
</style>
